import { Component, output, HostListener, inject, ViewChild, ElementRef, signal, effect, DestroyRef, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../api/services/auth.service';
import { User } from '../../data/user.data';
import { AvatarModule } from 'primeng/avatar';
import { MenubarModule } from 'primeng/menubar';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';
import { MenuItem, MessageService } from 'primeng/api';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { OverlayBadge } from 'primeng/overlaybadge';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { UserService } from '../../api/services/user.service';
import { Utils } from '../../utils/utils';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { DividerModule } from 'primeng/divider';
import { DrawerModule } from 'primeng/drawer';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterLink,
    AvatarModule, OverlayBadge,
    MenubarModule, MenuModule,
    InputTextModule, IconFieldModule, InputIconModule,
    ButtonModule,
    DividerModule,
    DrawerModule,
    ConfirmDialogComponent,
    UserAvatarComponent
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css'
})
export class NavbarComponent {
  @ViewChild(ConfirmDialogComponent) confirmDialog!: ConfirmDialogComponent;
  @ViewChild('userDropdown') userDropdownRef!: ElementRef;
  userService = inject(UserService);
  messageService = inject(MessageService);
  private readonly activatedRoute = inject(ActivatedRoute);
  searchQuery = signal('');
  openAuthModal = output<void>();
  isFixed = false;
  isMenuOpen = false;
  isUserMenuOpen = false;
  utils = Utils;
  userMenuItems: MenuItem[] = [];

  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  private isInitialized = false;

constructor() {
  effect(() => {
    const q = this.searchQuery().trim();

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    const currentUrl = this.router.url;
    const isHomepage =
      currentUrl === '/' ||
      currentUrl.startsWith('/?search=');

    if (!isHomepage) {
      this.isInitialized = true;
      return;
    }

    if (q.length >= 3) {
      this.searchTimeout = setTimeout(() => {
        this.router.navigate(['/'], {
          queryParams: { search: q }
        });
      }, 400);
    } else if (q.length === 0 && this.isInitialized) {
      this.router.navigate(['/'], {
        queryParams: {}
      });
    }

    this.isInitialized = true;
  });

  this.router.events.pipe(
    filter(e => e instanceof NavigationEnd),
    takeUntilDestroyed(this.destroyRef)
  ).subscribe((e) => {
    const url = (e as NavigationEnd).urlAfterRedirects;

    if (!url.startsWith('/?search=') && url !== '/') {
      this.searchQuery.set('');
    }
  });
}

  user$: Observable<User | null> = this.authService.user$;
  isLoggedIn$ = this.user$.pipe(map(u => !!u));

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isFixed = window.scrollY > 0;
    document.body.classList.toggle('navbar-fixed-padding', this.isFixed);
    this.isUserMenuOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.userDropdownRef && !this.userDropdownRef.nativeElement.contains(event.target)) {
      this.isUserMenuOpen = false;
    }
  }

  onLogoClick(): void {
    this.router.navigate(['/']).then(() => {
      window.location.reload();
    });
  }
  onSearch(): void {
    const q = this.searchQuery().trim();
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.router.navigate(['/'], { queryParams: q ? { search: q } : {} });
  }

  async onConfirmDeleteUser(): Promise<void> {
    try {
      const user = await firstValueFrom(this.user$);
      if (!user) return;

      await firstValueFrom(this.userService.deleteUser(user.id));
      this.messageService.add({ severity: 'success', summary: 'Confirmado', detail: 'Utilizador eliminado com sucesso' });
      this.authService.logout();
      this.router.navigateByUrl('/');
    }
    catch (error) {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao tentar eliminar produto. Por favor tente novamente' });
    }
  }

  toggleMenu(): void { this.isMenuOpen = !this.isMenuOpen; }
  toggleUserMenu(): void { this.isUserMenuOpen = !this.isUserMenuOpen; }

  onLogout(): void {
    this.authService.logout();
    if (this.router.url === '/') {
      window.location.reload();
    } else {
      this.router.navigateByUrl('/');
    }
    this.isUserMenuOpen = false;
  }

}
